import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const formatUnixSeconds = (value) => {
  if (!value && value !== 0) return 'N/A';
  try {
    const date = new Date(Number(value) * 1000);
    if (Number.isNaN(date.getTime())) throw new Error('Invalid date');
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return String(value);
  }
};

const clampValue = (value, min = 0, max = 100) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Math.max(min, Math.min(num, max));
};

const kmhToMph = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num * 0.621371;
};

const METRIC_CONFIG = [
  {
    key: 'acceleratorPedalPosition',
    label: 'Accelerator',
    unit: '%',
    min: 0,
    max: 100,
    type: 'bar'
  },
  {
    key: 'rpm',
    label: 'RPM',
    unit: 'rpm',
    min: 0,
    max: 2500,
    type: 'gauge'
  },
  {
    key: 'gpsSpeed',
    label: 'GPS Speed',
    unit: 'km/h',
    min: 0,
    max: 120,
    type: 'gauge',
    showMph: true
  },
  {
    key: 'canSpeed',
    label: 'CAN Speed',
    unit: 'km/h',
    min: 0,
    max: 120,
    type: 'gauge',
    showMph: true
  },
  {
    key: 'forwardVehicleSpeed',
    label: 'Forward Vehicle Speed',
    unit: 'km/h',
    min: 0,
    max: 120,
    type: 'gauge',
    showMph: true
  },
  {
    key: 'forwardVehicleDistanceSeconds',
    label: 'Forward Distance',
    unit: 's',
    min: 0,
    max: 6,
    type: 'bar'
  },
];

const deriveSeriesValue = (series = {}, key, index) => {
  const arr = series[key];
  if (!Array.isArray(arr) || index < 0 || index >= arr.length) return null;
  return arr[index];
};

const buildTimelinePoint = (event, index) => {
  const lat = deriveSeriesValue(event.series, 'latitude', index);
  const lng = deriveSeriesValue(event.series, 'longitude', index);
  const heading = deriveSeriesValue(event.series, 'heading', index);
  const gpsSpeed = deriveSeriesValue(event.series, 'gpsSpeed', index);
  const canSpeed = deriveSeriesValue(event.series, 'canSpeed', index);
  const forwardSpeed = deriveSeriesValue(event.series, 'forwardVehicleSpeed', index);

  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  const ts = event.initial_timestamp_seconds;
  const timestampSeconds = (typeof ts === 'number' ? ts : null) !== null
    ? ts + (index * event.sample_interval)
    : null;

  return {
    lat,
    lng,
    heading,
    gpsSpeed,
    canSpeed,
    forwardSpeed,
    timestampSeconds,
  };
};

const OnEventTelemetryViewer = ({ events = [], onActivePointChange }) => {
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const currentEvent = events[activeEventIndex] || null;
  const sampleCount = currentEvent?.sample_count || 0;
  const sampleInterval = currentEvent?.sample_interval || 1;
  const effectiveSampleIndex = Math.min(sampleIndex, Math.max(sampleCount - 1, 0));
  const eventSampleIndex = useMemo(() => {
    if (
      currentEvent &&
      typeof currentEvent.event_timestamp_seconds === 'number' &&
      typeof currentEvent.initial_timestamp_seconds === 'number' &&
      sampleInterval > 0
    ) {
      const delta = currentEvent.event_timestamp_seconds - currentEvent.initial_timestamp_seconds;
      if (delta >= 0) {
        return Math.min(Math.round(delta / sampleInterval), Math.max(sampleCount - 1, 0));
      }
    }
    return null;
  }, [currentEvent, sampleCount, sampleInterval]);

  const currentPoint = useMemo(() => {
    if (!currentEvent || sampleCount === 0) return null;
    return buildTimelinePoint(currentEvent, effectiveSampleIndex);
  }, [currentEvent, effectiveSampleIndex, sampleCount]);

  const callbackRef = useRef(onActivePointChange);
  useEffect(() => {
    callbackRef.current = onActivePointChange;
  }, [onActivePointChange]);

  const lastNotifiedPointRef = useRef(null);
  useEffect(() => {
    const callback = callbackRef.current;
    if (!callback) return;

    const prev = lastNotifiedPointRef.current;
    const changed = (() => {
      if (!prev && !currentPoint) return false;
      if ((prev && !currentPoint) || (!prev && currentPoint)) return true;
      return (
        prev.lat !== currentPoint.lat ||
        prev.lng !== currentPoint.lng ||
        prev.timestampSeconds !== currentPoint.timestampSeconds
      );
    })();

    if (!changed) return;

    lastNotifiedPointRef.current = currentPoint
      ? { lat: currentPoint.lat, lng: currentPoint.lng, timestampSeconds: currentPoint.timestampSeconds }
      : null;

    callback(currentPoint);
  }, [currentPoint]);

  useEffect(() => {
    if (!currentEvent || !isPlaying || sampleCount <= 1) return undefined;

    const intervalMs = Math.max(250, sampleInterval * 1000);
    const id = setInterval(() => {
      setSampleIndex((prev) => {
        if (prev >= sampleCount - 1) {
          return 0;
        }
        return prev + 1;
      });
    }, intervalMs);

    return () => clearInterval(id);
  }, [currentEvent, isPlaying, sampleInterval, sampleCount]);

  useEffect(() => {
    // Reset playback when the selected event changes
    setSampleIndex(0);
    setIsPlaying(false);
  }, [activeEventIndex]);

  if (!currentEvent || sampleCount === 0) {
    return (
      <div className="p-4 border rounded bg-muted/30 text-sm text-muted-foreground">
        No on-event telemetry data was captured for this device.
      </div>
    );
  }

  const currentTime = currentPoint?.timestampSeconds
    ? `${(currentPoint.timestampSeconds - currentEvent.initial_timestamp_seconds).toFixed(1)}s`
    : `${(effectiveSampleIndex * sampleInterval).toFixed(1)}s`;

  const handleSampleChange = (value) => {
    setSampleIndex(Number(value));
  };

  const handlePlayPause = () => {
    if (sampleCount <= 1) return;
    setIsPlaying((prev) => !prev);
  };

  const handleRestart = () => {
    setSampleIndex(0);
    setIsPlaying(false);
  };

  const handleEventChange = (event) => {
    setActiveEventIndex(Number(event.target.value));
  };

  const renderMetric = (config) => {
    const value = deriveSeriesValue(currentEvent.series, config.key, effectiveSampleIndex);
    if (value === null || value === undefined) return null;

    const clamped = clampValue(value, config.min, config.max);
    const percent = clamped === null
      ? 0
      : ((clamped - config.min) / (config.max - config.min)) * 100;

    if (config.type === 'gauge') {
      return (
        <div key={config.key} className="p-4 border rounded-lg bg-white/60 shadow-sm flex flex-col items-center gap-3">
          <div className="text-xs uppercase text-muted-foreground tracking-wide self-start">
            {config.label}
          </div>
          <div
            className="relative w-24 h-24 rounded-full"
            style={{
              background: `conic-gradient(var(--primary) ${percent * 1.8}deg, rgba(0,0,0,0.08) ${percent * 1.8}deg)`
            }}
          >
            <div className="absolute inset-[10px] rounded-full bg-white flex flex-col items-center justify-center text-center">
              <span className="text-lg font-semibold">{value}</span>
              <span className="text-[11px] text-muted-foreground">{config.unit}</span>
            </div>
          </div>
          {config.showMph && (
            <div className="text-[11px] text-muted-foreground">
              {(kmhToMph(value)?.toFixed(1) ?? '—')} mph
            </div>
          )}
          <div className="text-[11px] text-muted-foreground">
            Range {config.min} - {config.max}
          </div>
        </div>
      );
    }

    return (
      <div key={config.key} className="p-4 border rounded-lg bg-white/60 shadow-sm">
        <div className="text-xs uppercase text-muted-foreground tracking-wide">
          {config.label}
        </div>
        <div className="text-2xl font-bold flex items-baseline gap-2">
          {value}
          <span className="text-sm text-muted-foreground">{config.unit}</span>
        </div>
        <div className="h-2 bg-muted rounded-full mt-3">
          <div
            className="h-full bg-primary rounded-full transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <Card className="border shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-3">
          On-event telemetry playback
          {currentEvent.reason && (
            <Badge variant="secondary" className="uppercase tracking-wide">
              {currentEvent.reason}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="flex flex-wrap gap-4 text-xs">
          <span>Initial: {formatUnixSeconds(currentEvent.initial_timestamp_seconds)}</span>
          <span>Final: {formatUnixSeconds(currentEvent.final_timestamp_seconds)}</span>
          <span>Samples: {sampleCount}</span>
          <span>Interval: {sampleInterval}s</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {events.length > 1 && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">Event</span>
            <select
              className="border rounded-md px-2 py-1 text-sm bg-background"
              value={activeEventIndex}
              onChange={handleEventChange}
            >
              {events.map((event, idx) => (
                <option key={idx} value={idx}>
                  #{idx + 1} • {event.reason || 'Unknown reason'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3 w-full">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePlayPause}
              disabled={sampleCount <= 1}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRestart}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <div className="relative flex-1">
              <input
                type="range"
                min={0}
                max={Math.max(sampleCount - 1, 0)}
                value={effectiveSampleIndex}
                onChange={(event) => handleSampleChange(event.target.value)}
                className="w-full accent-primary"
              />
              {eventSampleIndex !== null && sampleCount > 1 && (
                <span
                  className="absolute -top-2 w-2 h-2 rounded-full bg-red-500"
                  style={{
                    left: `${(eventSampleIndex / Math.max(sampleCount - 1, 1)) * 100}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  title="Event trigger"
                />
              )}
            </div>
            <span className="text-xs font-mono text-muted-foreground min-w-[60px] text-right">
              {currentTime}
            </span>
          </div>
          {eventSampleIndex !== null && (
            <div className="text-xs text-muted-foreground">
              Event at ~{eventSampleIndex * sampleInterval}s ({eventSampleIndex + 1}/{sampleCount})
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {METRIC_CONFIG.map(renderMetric)}
        </div>

        <div className="border rounded-lg p-4 bg-muted/30 text-sm">
          <div className="font-semibold mb-2">Timeline snapshot</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
            <div>
              <span className="block text-[11px] uppercase tracking-wide">Heading</span>
              <span className="font-semibold text-foreground">{currentPoint?.heading ?? 'N/A'}</span>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-wide">GPS Speed</span>
              <span className="font-semibold text-foreground">
                {currentPoint?.gpsSpeed !== null && currentPoint?.gpsSpeed !== undefined
                  ? `${currentPoint.gpsSpeed} km/h (${kmhToMph(currentPoint.gpsSpeed)?.toFixed(1) ?? '—'} mph)`
                  : 'N/A'}
              </span>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-wide">CAN Speed</span>
              <span className="font-semibold text-foreground">
                {currentPoint?.canSpeed !== null && currentPoint?.canSpeed !== undefined
                  ? `${currentPoint.canSpeed} km/h (${kmhToMph(currentPoint.canSpeed)?.toFixed(1) ?? '—'} mph)`
                  : 'N/A'}
              </span>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-wide">Timestamp</span>
              <span className="font-semibold text-foreground">
                {currentPoint?.timestampSeconds
                  ? formatUnixSeconds(currentPoint.timestampSeconds)
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OnEventTelemetryViewer;
